import React, { useState, useEffect, useRef, useMemo } from "react";
import ReportExporter from "../utils/ReportExporter";
import StagingTable from "../utils/StagingTable";
import { socket } from "../utils/socket";
import { useDashboard } from "./dashboardStore";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
const VIVACE_USER_ID = "26"; // Check SQL for this entry if it ever changes

const CostAccounting = () => {
	const pageSize = 10;

	const [users, setUsers] = useState([]);
	const [costs, setCosts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);
	const [errors, setErrors] = useState({});
	const [showModal, setShowModal] = useState(false);
	const [newCost, setNewCost] = useState({
		userId: "",
		userName: "",
		category: "",
		advance: "",
		poId: "",
		amount: "",
		date: "",
		description: "",
	});
	const [filter, setFilter] = useState({ start: "", end: "", category: "", advance: "", userName: "" });

     const parentRef = useRef(null);
     const [showStaging, setShowStaging] = useState(false);
     const stagingRef = useRef(null);
    
     // Scroll to staging table or parent based on toggle
     useEffect(() => {
         if (showStaging && stagingRef.current) {
             stagingRef.current.scrollIntoView({ behavior: "smooth" });
         }
    
         if (!showStaging && parentRef?.current) {
             parentRef.current.scrollIntoView({ behavior: "smooth" });
         }
     }, [showStaging]);
         
    const userMapRef = useRef({});
    
    // Update the ref whenever users change
    useEffect(() => {
        userMapRef.current = Object.fromEntries(users.map(u => [u.id, u.userName]));
    }, [users]);

  
    // Socket: handle new cost
    useEffect(() => {
        if (!socket) return;

        const handleNewQuotation = (data) => {
        	if (data.type === "singleNewCost") {
                    const added = data.newCost;
                    setCosts(prevCosts => [
                      { 
                        id: added.id,
                        userId: added.user_id,
                        userName: userMapRef.current[added.user_id] || "Refresh Page",
                        category: added.category,
                        advance: added.advance,
                        amount: parseFloat(added.amount),
                        date: added.date,
                        description: added.description
                      },
                      ...prevCosts
                    ]);

            }
            
            if (data.type === "costDeleted") {
                setCosts(prevCosts => prevCosts.filter(q => q.id !== Number(data.id)));
            }

            if (data.type === "batchCosts") { //allRows
            
                const mappedRows = data.rows.map(cost => ({
                	...cost,
                userName: userMapRef.current[cost.user_id] || "Refresh Page"
                }));
   
                
                // Prepend new rows to existing costs
                setCosts(prev => [...mappedRows, ...prev]);
            }
        }

        socket.on("alertCosts", handleNewQuotation);
        return () => {
            socket.off("alertCosts", handleNewQuotation);
        };
    }, [socket]);
    

	// Validate form before adding cost
	const validateNewCost = (cost) => {
		const errs = {};

		if (!cost.userId) errs.userId = "Employee is required";
		if (!cost.category) errs.category = "Category is required";

		const amount = parseFloat(cost.amount);
		if (!cost.amount) errs.amount = "Amount is required";
		else if (isNaN(amount) || amount <= 0) errs.amount = "Amount must be a positive number";

		if (!cost.date) errs.date = "Date is required";
		if (!cost.description) errs.description = "Description is required";

		// Restrict advance to Salary category only
		if (cost.category !== "Salary" && cost.advance) {
			errs.advance = "Don't enter advance if category is not Salary (Capital S letter)";
			errs.category = "This field must be exactly 'Salary' or clear the advance field";
		}

		if (cost.category !== "Purchase Order" && cost.userId != VIVACE_USER_ID && cost.poId) {
			errs.poId = "Don't enter Purchase Order Id if category is not Purchase Order";
			errs.category = "This field must be exactly 'Purchase Order' or clear the poID field";
			errs.userId = "The cost incurrer must be Vivace Company for Purchase Orders";
		}
		
		// Salaries must be under Vivace
		if (cost.category === "Salary" && cost.userId !== VIVACE_USER_ID) {
			errs.description = [
				"⚠️ Salaries must be recorded under Vivace Company.",
				"Please do the following:",
				"1. Set the name to 'Vivace Company' (since salaries are paid by Vivace).",
				"2. In the description, mention who received the salary, e.g., 'Salary of Andrew'."
			];
		}

		if (cost.description && cost.description.length > 200)
			errs.description = "Description cannot exceed 200 characters";

		return errs;
	};
const didInitialFetch = useRef(false);

	// Subscribe to dashboard store slice
const usersDat = useDashboard(state => state.data?.users);

// -------------------------------------
// Process users data (with 24h localStorage cache)
// -------------------------------------
useEffect(() => {
	// Wait until store has data
	if (!usersDat) return;

	const CACHE_KEY = "usersCache";
	const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24h

	// Check localStorage first
	const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
	const now = Date.now();
	if (cached && now - cached.timestamp < CACHE_DURATION) {
		setUsers(cached.data || []);
		return; // use cache, skip processing
	}

	const processUsers = () => {
		try {


			// Save to state
			setUsers(usersDat);

			// Cache locally for 24h
			localStorage.setItem(
				CACHE_KEY,
				JSON.stringify({ data: usersDat, timestamp: Date.now() })
			);
		} catch (err) {
			console.error("Error processing users:", err);
		}
	};

	processUsers();
}, [usersDat]); // Reactive: runs whenever store updates

// Subscribe to dashboard store slice
const costsDat = useDashboard(state => state.data?.costs);

// -------------------------------------
// Process costs data
// -------------------------------------
useEffect(() => {
  // Wait until store has data
  if (!costsDat) return;

  try {
    // Save to state directly
	setCosts(costsDat.map(c => ({ ...c, amount: parseFloat(c.amount) })));
  } catch (err) {
    console.error("Error processing costs:", err);
    setCosts([]);
  }
}, [costsDat]); // Reactive: runs whenever store updates




	// Add new cost
	const handleAddCost = async (cost) => {
		const errs = validateNewCost(cost);
		if (Object.keys(errs).length > 0) {
			setErrors(errs);
			return;
		}
		setErrors({});

// todo1
		cost.userName = userMapRef[cost.userId] || "Unknown";

		try {
			const res = await fetch(`${apiBaseUrl}/api/costs`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					userId: cost.userId,
					userName: cost.userName,
					category: cost.category,
					advance: cost.advance,
                    poId: cost.poId || null,
					amount: parseFloat(cost.amount),
					date: cost.date,
					description: cost.description || "",
				}),
			});

			if (!res.ok) throw new Error("Failed to add cost");


			setPage(1);
			setShowModal(false);
			setNewCost({ userId: "", userName: "", category: "", advance: "", 
            			poId: "", amount: "", date: "", description: "" });
		} catch (err) {
			console.error(err);
			alert("Failed to add cost");
		}
	};

	// Delete a cost entry
	const handleDeleteCost = async (id) => {
		if (!window.confirm("⚠️ Are you sure you want to delete this cost?")) return;
		try {
			const res = await fetch(`${apiBaseUrl}/api/costs/${id}`, { method: "DELETE" });
			if (!res.ok) throw new Error("Failed to delete cost");

		} catch (err) {
			console.error(err);
			alert("Failed to delete cost");
		}
	};

	// Filter and paginate
	const filteredCosts = costs.filter(c =>
		Object.values(c).some(v => String(v).toLowerCase().includes(search.toLowerCase()))
	);
	const totalPages = Math.ceil(filteredCosts.length / pageSize);
	const currentItems = filteredCosts.slice((page - 1) * pageSize, page * pageSize);

	return (
		<div className="w-full p-4">
		
				            {/* Top row: Download & Used Units/Regions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-5">
                {/* Left: Units and Regions */}
                <div className="p-2 border rounded-xl bg-white shadow-sm h-full max-w-4xl">
                    <h2 className="text-lg font-semibold text-gray-800 mb-1">
                        Used Categories
                        </h2>
                    <span className="text-xs text-gray-400 mb-3 block">
                        Please use same letters as below or add new unit.
                    </span>

                    {/* Units */}
                    <div className="flex flex-wrap gap-2">
						{[...new Set(costs.map(i => i.category))].map((type, idx) => (
                            <span
                                key={idx}
                                className="px-3 py-1 rounded-full bg-green-100 text-green-700 font-bold text-sm shadow-sm"
                            >
                                {type}
                            </span>
                        ))}
                    </div>

                </div>


    			{/* Export button */}
    			<ReportExporter
    				title="Export Costs"
    				data={costs}
    				showDate={false}
    				showSummary={true}
    				columns={[
    					{ label: "Employee", field: "userName" },
    					{ label: "Category", field: "category" },
    					{ label: "Amount", field: "amount" },
    					{ label: "Date", field: "date" },
    					{ label: "Description", field: "description" },
    						{ label: "PO ID", field: "poId" }, 

    				]}
    				filterableFields={{
    					category: "Categories",
    					userName: "Employees"
    				}}
    			/>
            </div>
		
		


			{/* Search and Add */}
			<div  ref={parentRef}  className="flex flex-col sm:flex-row items-center justify-between mb-4 gap-2">
				<input
					type="text"
					placeholder="Search costs..."
					className="px-3 py-2 border rounded-md flex-1"
					value={search}
					onChange={e => { setSearch(e.target.value); setPage(1); }}
				/>
				
                <button
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
                    onClick={() => setShowStaging(prev => !prev)}
                >
                    Batch Upload
                    <span
                        className={`ml-2 transform transition-transform duration-200 ${
                            showStaging ? "rotate-180" : "rotate-0"
                        }`}
                    >
                        ▼
                    </span>
                </button>
                
				
				<button className="px-4 py-2 bg-blue-600 text-white rounded cursor-pointer" onClick={() => setShowModal(true)}>
					Add Cost
				</button>
			</div>

			{/* Cost table */}
			<div className="overflow-x-auto rounded-md border border-gray-200 mb-6">
				{loading ? (
					<div className="text-center py-10 text-gray-500">Loading costs...</div>
				) : (
					<table className="min-w-full divide-y divide-gray-100 text-sm">
						<thead className="bg-gray-50">
							<tr>
								{["Employee","Category","Amount","Date","Description"].map(h => (
									<th key={h} className="px-3 py-2 text-center font-bold">{h}</th>
								))}
								<th className="px-3 py-2 text-center">Actions</th>
								<th className="px-3 py-2 text-center">Advance</th>
								<th className="px-3 py-2 text-center">PO ID</th>
							</tr>
						</thead>
						<tbody>
							{currentItems.length === 0 ? (
								<tr>
									<td colSpan={6} className="px-3 py-4 text-center text-gray-500 italic">No costs found</td>
								</tr>
							) : currentItems.map(c => {
								const formattedDate = new Date(c.date).toLocaleDateString("en-GB", {
									day: "numeric",
									month: "long",
									year: "numeric",
								});
								return (
									<tr key={c.id} className="hover:bg-gray-300 even:bg-gray-200 odd:bg-white">
										<td className="px-3 py-2">{c.userName}</td>
										<td className="px-3 py-2">{c.category}</td>
										<td className="px-3 py-2">
											{c.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED
										</td>
										<td className="px-3 py-2">{formattedDate}</td>
										<td className="px-3 py-2">{c.description}</td>
										<td className="px-3 py-2 text-center">
											<button className="px-2 py-1 bg-red-600 text-white rounded cursor-pointer" onClick={() => handleDeleteCost(c.id)}>
												Delete
											</button>
										</td>
										<td className="px-3 py-2 text-center">
											{c.category === "Salary"
												? <span>{c.advance ? `${c.advance.toLocaleString()} AED` : "0 AED"}</span>
												: <span className="italic text-gray-500">Not Salary</span>}
										</td>
										<td className="px-3 py-2 text-center">
										{c.poId}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				)}

				{/* Pagination */}
				{totalPages > 1 && (
					<div className="flex justify-center items-center gap-2 mt-3">
						<button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1} className="px-3 py-1 border rounded">Prev</button>
						<span>Page {page} of {totalPages}</span>
						<button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page===totalPages} className="px-3 py-1 border rounded">Next</button>
					</div>
				)}
			</div>

			{/* Modal for adding cost */}
			{showModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
					<div className="bg-white rounded-lg w-full max-w-md p-6 relative shadow-lg pointer-events-auto">
						<h2 className="text-xl font-bold mb-4">Add New Cost</h2>
						<div className="flex flex-col gap-3">
							<select
								className="px-3 py-2 border rounded"
								value={newCost.userId}
								onChange={e => setNewCost({ ...newCost, userId: e.target.value })}
							>
								<option value="" disabled>Select Employee</option>
								{users.map(u => (
									<option key={u.id} value={u.id}>{u.userName}</option>
								))}
							</select>
							{errors.userId && <p className="text-red-600 text-sm">{errors.userId}</p>}

							<input type="text" placeholder="Category" className="px-3 py-2 border rounded" value={newCost.category} onChange={e => setNewCost({ ...newCost, category: e.target.value })} />
							{errors.category && <p className="text-red-600 text-sm">{errors.category}</p>}

							<input type="number" placeholder="Amount" className="px-3 py-2 border rounded" value={newCost.amount} onChange={e => setNewCost({ ...newCost, amount: e.target.value })} />
							{errors.amount && <p className="text-red-600 text-sm">{errors.amount}</p>}

							<input
								type="number"
								placeholder="Advance (if Salary)"
								className={`px-3 py-2 border rounded ${errors.advance ? "border-red-600 bg-red-100" : "border-gray-300"}`}
								value={newCost.advance}
								onChange={e => setNewCost({ ...newCost, advance: e.target.value })}
							/>
							{errors.advance && <p className="text-red-600 text-sm">{errors.advance}</p>}

                            <input
                            	type="number"
                            	placeholder="Purchase Order ID (optional)"
								className={`px-3 py-2 border rounded ${errors.advance ? "border-red-600 bg-red-100" : "border-gray-300"}`}
                            	value={newCost.poId}
                            	onChange={e => setNewCost({ ...newCost, poId: e.target.value })}
                            />
							{errors.poId && <p className="text-red-600 text-sm">{errors.poId}</p>}


							<input type="date" className="px-3 py-2 border rounded" value={newCost.date} onChange={e => setNewCost({ ...newCost, date: e.target.value })} />
							{errors.date && <p className="text-red-600 text-sm">{errors.date}</p>}

							<textarea placeholder="Description" className="px-3 py-2 border rounded" value={newCost.description} onChange={e => setNewCost({ ...newCost, description: e.target.value })} />
							{errors.description && (
								<div className="text-red-600 text-sm mt-1">
									{Array.isArray(errors.description)
										? errors.description.map((line, i) => <p key={i}>{line}</p>)
										: <p>{errors.description}</p>}
								</div>
							)}
						</div>
						<div className="flex justify-end gap-2 mt-4">
							<button className="px-4 py-2 bg-gray-300 rounded cursor-pointer" onClick={() => setShowModal(false)}>Cancel</button>
							<button className="px-4 py-2 bg-blue-600 text-white rounded cursor-pointer" onClick={() => handleAddCost(newCost)}>Add</button>
						</div>
					</div>
				</div>
			)}

			{/* Staging Table */}
    	<StagingTable
    		visible={showStaging}
    		ref={stagingRef}
    		setVisible={setShowStaging}
    		apiProp={`${apiBaseUrl}/api/costs/batch`}
    		entryDict={Object.fromEntries(
            ["userId", "category", "advance", "poId", "amount", "description"].map((key) => {
    				let type = "input";
    				let additionalTypes = null;
                    let options;
            		let defaultValue = "";

            		if (key === "advance") {
                        additionalTypes = "number";
                		defaultValue = 0; 
            		}

    				if (key === "amount") additionalTypes = "number";
    				if (key === "date") additionalTypes = "date";
    				
    				if (key === "userId") {
                        type = "select";
                    	options = users.map(u => ({
                    		value: (u.id),
                    		label: u.userName
                    	}));
    				}
    
    				const labelMap = {
    					userId: "Employee ID",
    					category: "Category",
    					advance: "Advance (AED)",
    					amount: "Amount (AED)",
    					date: "Date",
    					description: "Description",
    					poId: "Purchase Order ID"
    				};
    
                    return [key, { type, options, label:labelMap[key], additionalTypes, defaultValue }];
    			})
    		)}
    		
    		validateRow={(row) => {
        		if (row.category !== "Salary" && Number(row.advance) > 0) {
        			return "Advance is only allowed for Salary category.";
        		}
	        	if (row.category === "Salary" && row.userId != VIVACE_USER_ID) {
        			return "If category is salary, the cost incurror should be Vivace Company.";
        		}
		
        		return null;
        	}}
            		
    	/>


		</div>
	);
};

export default CostAccounting;
